import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;


type ThisProjectMessages = {
    optIn: string,
    ruleOptIn: string,
    collectInfoStepFooter: string,
    errorId: {
        invalidPurchaseSupport: string,
        invalidProjectName: string,
        invalidOsiLicense: string,
        invalidPublicProject: string,
        invalidProjectDescription: string,
    },
    step: {
        personalInfo: {
            purchaseSupportLabel: string,
            purchaseSupportPlaceholder: string,
            projectDescriptionPlaceholder: string,
            publicProjectPlaceholder: string,
            OSIlicensePlaceholder: string,
            projectNameLabel: string,
            osiLicenseLabel: string,
            publicProjectLabel: string,
            projectDescriptionLabel: string,
            verifyProjectPlaceholder: string,
            ruleOptInLabel: string,
            projectDescriptionCounter: string,
            productSupportChoices: {
                Yes: string,
                No: string,
            }
        },
        docUpload: {
            acceptableUploads: {
                publiclyVisibleHeader: string,
                osiHeader: string,
                one: string,
                two: string,
                three: string,
                instructions: string,
                note: string,
            }
        }
    },
};


export const messagesEN: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    schoolName: 'Institution Name',
    optIn: 'I would like to receive emails from GitLab about its product, services, and events.',
    ruleOptIn: 'I certify that project maintainers are not seeking to make profit from this project by, for example, selling services or higher tiers. <a href="https://about.gitlab.com/handbook/marketing/community-relations/opensource-program/#who-qualifies-for-the-gitlab-for-open-source-program" class="sid-link" target="_blank">Make sure your use case qualifies.</a>',
    infoShared1: 'By submitting this form, you understand that your information will be shared with GitLab by SheerID and used for verification purposes.<br><br>For more information please see the <a href="https://about.gitlab.com/privacy/" class="sid-link" target="_blank" rel="noopener noreferrer">GitLab\'s Privacy Policy</a>.<br><br>If you are accepted into the GitLab for Open Source program, you will be subject to <a href="https://about.gitlab.com/handbook/legal/opensource-agreement/" class="sid-link" target="_blank" rel="noopener noreferrer">GitLab\'s terms and conditions</a>. All use of GitLab products must comply with United States export control and economic sanction laws.<br><br>',
    infoShared2: ' ',
    informationTransferredToUS: 'Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy.<br><br>',
    countryPlaceholder: 'Select...',
    verifyMyMembershipStatus: 'Submit',
    poweredBy: 'Verification services powered by SheerID.',
    error: 'Unable to verify',
    errorId: {
        docReviewLimitExceeded:'We have reviewed your application and are unable to accept your project into the GitLab for Open Source Program.<br><br>This might be because: <br><br>● The documentation you supplied is insufficient <br><br>● Your project does not meet program eligibility criteria <br><br>If you have additional questions about your application, please contact opensource@gitlab.com . Include the email address you supplied when submitting your application.',
        invalidPurchaseSupport: 'Invalid option',
        invalidProjectName: 'Invalid project name',
        invalidOsiLicense: 'Invalid OSI approved license',
        invalidPublicProject: 'Invalid project',
        invalidProjectDescription: 'Invalid project description',
    },
    step: {
        personalInfo: {
            title: 'Apply for the GitLab for Open Source Program',
            subtitle: 'Features of GitLab Ultimate, including 50,000 CI/CD minutes, are free for qualifying open source projects. New and renewing members should use this form to verify eligibility. <a href="https://about.gitlab.com/handbook/marketing/community-relations/community-programs/opensource-program/#how-are-gitlab-for-open-source-program-applications-processed" class="sid-link-custom-howdoesverificationwork-nounderline" rel="noopener noreferrer" target="_blank">How does verification work?</a>',
            purchaseSupportLabel: 'Do you want to purchase support?',
            purchaseSupportPlaceholder: 'Select...',
            projectDescriptionPlaceholder: 'Describe your project in 1-3 sentences.',
            publicProjectPlaceholder: 'Add a link to your publicly visible project on GitLab.\nExample: gitlab.com/your-project-namespace',
            OSIlicensePlaceholder: 'Link to a license file in your GitLab instance',
            projectNameLabel: 'Open source organization or project name',
            osiLicenseLabel: 'OSI approved license',
            publicProjectLabel: 'Publicly visible project on GitLab',
            projectDescriptionLabel: 'Project description',
            ruleOptInLabel: 'Not Seeking Profit',
            verifyProjectPlaceholder: '(Optional) Please provide additional details to help us verify you aren\'t seeking profit with your project.',
            projectDescriptionCounter: 'Character Count: {count}/256',
            productSupportChoices: {
                Yes: 'true',
                No: 'false',
            },
        },
        success: {
            title: 'Success!<br>You\'ve been verified',
            emailNotification: 'We have emailed you a personal coupon code and directions to obtain your subscription. It is unique to you and can only be used once.<br><br>Look for the email from verify@sheerid.com.',
        },
        pending: {
            turnaroundTime: 'GitLab is reviewing your documents to confirm your eligibility for the program. This process typically takes 3 to 5 business days. In rare times of high volume, we may require up to 10 business days.',
            subtitle2: 'If you have any questions, please let us know via opensource@gitlab.com.',
            subtitle3: 'Thank you!<br><br><span class="pending-footer">Verification service provided by SheerID and GitLab.</span>',
        },
        docUpload: {
            submitButtonLabel: 'Submit',
            title: 'Complete your application',
            subtitle: 'To complete your application, upload screenshots of your GitLab instance so we can verify your eligibility.<br><br><b>Please upload the following three screenshots:',
            acceptableUploads: {
                one: 'License file as it appears on your project\'s detail page',
                two: 'Text file uploaded to GitLab containing your license',
                three: 'Your publicly visible settings under the \"Visibility, project features, permissions\" section of the project\'s admin panel',
                instructions: '<a href="https://docs.gitlab.com/ee/subscriptions/community_programs.html#gitlab-for-open-source" class="sid-link" target="_blank" rel="noopener noreferrer">See instructions for taking required screenshots</a>',
                note: 'Note: "Document" refers to all three screenshots you have submitted. For more information on how to submit the needed documentation, please visit <a href="https://docs.gitlab.com/ee/subscriptions/community_programs.html#gitlab-for-open-source" class="sid-link" target="_blank" rel="noopener noreferrer">Gitlab docs</a>.'
            },
        },
    },
};
